/**
 * Embedded Mode
 * Initialization for embedded search on sport sub-pages.
 * Uses user's cached geo location, no URL sync, no localStorage writes.
 */

import { state } from '../core/state.js';
import { elements } from '../core/elements.js';
import { performSearch } from '../algolia/search-executor.js';
import { readUserGeoFromCache } from '../decision-engine/user-geo.js';
import { seasonToDateRange } from '../utils/date-utils.js';
import { handleGeocoding } from '../geocoding/geocode-handler.js';

/**
 * Check if the current page uses embedded search mode
 * @returns {boolean}
 */
export function isEmbeddedMode() {
  return !!document.querySelector('[data-algolia-mode="embedded"]');
}

/**
 * Read embedded search configuration from data attributes
 * @returns {Object|null}
 */
export function getEmbeddedConfig() {
  const el = document.querySelector('[data-algolia-mode="embedded"]');
  if (!el) return null;
  return {
    sportSlug:    el.dataset.sportSlug   || null,
    presetSport:  el.dataset.presetSport || null, // overrides sportSlug if set
    presetTypes:  el.dataset.presetTypes
                    ? el.dataset.presetTypes.split(',').filter(Boolean)
                    : [],
    presetGender: el.dataset.presetGender || null, // 'male' | 'female' | null
    presetSeason: el.dataset.presetSeason || null, // 'winter' | 'spring' | 'summer' | 'fall' | null
    presetAge:    el.dataset.presetAge    || null,  // numeric string or null
    presetLive:   el.dataset.presetLive === '1',   // true if live camps only
    presetCustomValue: el.dataset.presetCustomValue || null, // custom heading value
    presetBrands: el.dataset.presetBrands
                    ? el.dataset.presetBrands.split(',').filter(Boolean)
                    : [],
    presetLocation: el.dataset.presetLocation || null, // location to geocode (state name or "City, State")
  };
}

/**
 * Initialize embedded search mode.
 * Sets sport/type/gender/age/live filters from data attributes,
 * coordinates from user's cached geo.
 */
export async function initEmbeddedMode() {
  const config = getEmbeddedConfig();
  if (!config) return;

  console.log('[Embedded] Initializing with config:', config);

  // presetSport overrides sportSlug when both are present
  const effectiveSport = config.presetSport || config.sportSlug;
  if (effectiveSport) {
    state.sportSlug = [effectiveSport];
    console.log('[Embedded] Sport:', effectiveSport, config.presetSport ? '(from preset)' : '(from page)');

    // Update search form sport picker to show the selected sport visually
    if (config.presetSport && window.SportsSelection?.setValues) {
      window.SportsSelection.setValues([effectiveSport]);
    }
  }

  // Preset camp types — filter-builder.js always adds _empty automatically
  if (config.presetTypes.length) {
    state.types = config.presetTypes;

    // Sync to search button so search-form.js can pass types in URL
    const searchButton = document.querySelector('.rd-search-form__button');
    if (searchButton) {
      searchButton.dataset.types = config.presetTypes.join(',');
    }
  }

  // Preset gender — 'male' includes coed automatically (gender:"male" OR gender:"all")
  if (config.presetGender === 'male' || config.presetGender === 'female') {
    state.gender = [config.presetGender];
  }

  // Preset age
  if (config.presetAge) {
    state.age = config.presetAge;
  }

  // Preset season → date range + search form UI
  if (config.presetSeason) {
    const dateRange = seasonToDateRange(config.presetSeason);
    if (dateRange) {
      state.beginDates = dateRange.beginDates;
      state.endDates = dateRange.endDates;
      console.log('[Embedded] Preset season:', config.presetSeason, '→', dateRange);

      // Sync dates to search button so search-form.js can pass them in URL
      const searchButton = document.querySelector('.rd-search-form__button');
      if (searchButton) {
        searchButton.dataset.beginDates = dateRange.beginDates;
        searchButton.dataset.endDates = dateRange.endDates;
      }

      // Update search form date picker to show the selected season visually
      if (window.DatesSelection?.selectSeason) {
        window.DatesSelection.selectSeason(config.presetSeason);
      }
    }
  }

  // Preset live camps
  if (config.presetLive) {
    state.live = true;

    // Sync to search button so search-form.js can pass live in URL
    const searchButton = document.querySelector('.rd-search-form__button');
    if (searchButton) {
      searchButton.dataset.live = '1';
    }
  }

  // Preset brands
  if (config.presetBrands.length) {
    state.brand = config.presetBrands;

    // Sync to search button so search-form.js can pass brand in URL
    const searchButton = document.querySelector('.rd-search-form__button');
    if (searchButton) {
      searchButton.dataset.brand = config.presetBrands.join(',');
    }
  }

  // Custom heading value (e.g. "Overnight") and sport title for embedded heading
  if (config.presetCustomValue) {
    state.embeddedCustomValue = config.presetCustomValue;
  }
  // Store sport title for heading — resolve from sport items in DOM
  if (effectiveSport) {
    const sportItem = document.querySelector(
      `.rd-search-modal__sport-item[data-sport-slug="${effectiveSport}"]`
    );
    if (sportItem) {
      state.embeddedSportTitle = sportItem.textContent.trim();
    }
  }

  // Preset location — geocode to get coordinates and fill the zip search input
  if (config.presetLocation) {
    state.zip = config.presetLocation;
    state.embeddedPresetLocation = config.presetLocation;
    if (elements.zipInput) elements.zipInput.value = config.presetLocation;
    if (elements.mobileZipInput) elements.mobileZipInput.value = config.presetLocation;
    // Tag location-log events fired during the preset geocode as 'listing'
    // (vs user-typed 'search') — see logLocationEvent in camps-helpers.js
    window.CampsUtils?.setLocationLogSource?.('listing');
    try {
      await handleGeocoding(config.presetLocation);
    } finally {
      window.CampsUtils?.setLocationLogSource?.(null);
    }
    console.log('[Embedded] Preset location geocoded:', config.presetLocation, '→', state.coordinates);
  } else {
    // Fall back to user's cached geo when no preset location
    const userGeo = readUserGeoFromCache();
    if (userGeo?.lat != null && userGeo?.lng != null) {
      state.coordinates = { lat: userGeo.lat, lng: userGeo.lng };
      state.geocodeResult = { recognized: true, rawInput: userGeo.city || userGeo.state || '' };
      console.log('[Embedded] Using user geo from cache:', state.coordinates);
    } else {
      console.warn('[Embedded] No user geo in cache, search will run without coordinates');
    }
  }

  await performSearch();
}

console.log('📦 Embedded mode module loaded');
